import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Link2, Megaphone, RefreshCw, Store, Tag } from 'lucide-react';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/contexts/AuthContext';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { useToast } from '@/components/ui/Toast';
import {
  connectionsApi,
  type ConnectionAccount,
  type AdsVendorLinkProfile,
  isAmazonReauthRequiredError,
} from '@/api/client';
import { AdsVendorLinkPicker } from '@/components/AdsVendorLinkPicker';
import { ADS_VENDOR_LINKS_QUERY_KEY } from '@/utils/adsVendorLinks';
import {
  AmazonOauthBanner,
  CONNECTIONS_AMAZON_REGION_OPTIONS,
  ConnectAmazonButton,
  type AmazonAdsRealm,
} from '@/components/AmazonOauthConnections';
import {
  FlipkartConnectionsPanel,
  FLIPKART_OAUTH_CONNECTIONS_QUERY_KEY,
} from '@/components/FlipkartOauthConnections';
import {
  FlipkartAdsConnectionsPanel,
  FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY,
  FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY,
} from '@/components/FlipkartAdsOauthConnections';
import { SpVerticalConnectionsPanel } from '@/components/SpVerticalConnectionsPanel';
import { SP_API_OAUTH_CONNECTIONS_QUERY_KEY } from '@/components/SpApiOauthConnections';
import { ConnectionAccountCard } from '@/components/connections/ConnectionAccountCard';
import { amazonAccountRegion, groupByAdvertisingRegion } from '@/api/advertisingAccountRegions';
import { flipkartAdsConnectMessage } from '@/api/flipkartAdsOauth';
import { cn } from '@/utils/cn';
import { ANALYSIS_ROUTE } from '@/components/analysis/reportAnalysisUi';

const CONNECTIONS_QUERY_KEY = ['connection-accounts'] as const;

type ConnectionsTab = 'ads' | 'seller' | 'vendor';

const TABS: { id: ConnectionsTab; label: string; icon: typeof Megaphone }[] = [
  { id: 'ads', label: 'Ads', icon: Megaphone },
  { id: 'seller', label: 'Seller', icon: Tag },
  { id: 'vendor', label: 'Vendor', icon: Store },
];

const connectButtonClass =
  'h-10 shrink-0 rounded-lg bg-brand-ink px-4 text-sm font-medium text-white shadow-sm hover:bg-chrome-border';

function parseTab(value: string | null): ConnectionsTab {
  if (value === 'seller' || value === 'vendor' || value === 'ads') return value;
  return 'ads';
}

function statusLabel(account: ConnectionAccount): string {
  if (account.status === 'syncing') return 'Setting up…';
  if (account.status === 'failed') return 'Setup failed';
  if (account.status === 'ready' || account.alreadySynced) return 'Already synced';
  return 'Needs setup';
}

function ConnectionEmptyMark({ src }: { src: string }) {
  return (
    <div className="relative h-16 w-20" aria-hidden>
      <div className="flex h-14 w-16 flex-col overflow-hidden rounded-lg border border-[#e4e7ec] bg-white shadow-sm">
        <div className="flex h-3.5 items-center gap-1 border-b border-[#eef0f3] bg-[#f7f8fa] px-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#d5dbe3]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#d5dbe3]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#d5dbe3]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <img src={src} alt="" className="h-6 w-6 object-contain" />
        </div>
      </div>
      <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full border border-[#e4e7ec] bg-white shadow-sm">
        <Link2 className="h-3 w-3 text-brand-deep" />
      </span>
      <span className="absolute -right-1 top-1 text-[10px] leading-none text-amber-400">✦</span>
    </div>
  );
}

function statusClass(account: ConnectionAccount): string {
  if (account.status === 'syncing') return 'text-brand';
  if (account.status === 'failed') return 'text-warning';
  if (account.status === 'ready' || account.alreadySynced) return 'text-foreground-subtle';
  return 'text-foreground-muted';
}

function CountryHeading({ code, label }: { code: string; label: string }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-medium text-brand-ink">
      <span className="inline-flex h-6 w-8 items-center justify-center rounded-md bg-[#f3f4f6] text-[11px] font-semibold tracking-wide text-foreground-muted">
        {code}
      </span>
      <span>{label}</span>
    </h3>
  );
}

export function ConnectionsPage() {
  const { user, hasPermission, isSuperAdmin } = useAuth();
  const showAdmin = hasPermission('admin.users.read');
  const isDemo = Boolean(user?.is_demo);
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<ConnectionsTab>(() => parseTab(searchParams.get('tab')));
  const [preferredSpCredentialId, setPreferredSpCredentialId] = useState<string | null>(null);
  const [amazonRegion, setAmazonRegion] = useState('IN');
  const amazonRealm: AmazonAdsRealm =
    CONNECTIONS_AMAZON_REGION_OPTIONS.find((option) => option.value === amazonRegion)?.realm ?? 'EU';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...CONNECTIONS_QUERY_KEY, user?.id],
    queryFn: async () => {
      const res = await connectionsApi.listAccounts();
      if (!res.success) throw new Error('Failed to load connections');
      return res.data;
    },
    enabled: Boolean(user?.id) && tab === 'ads',
    refetchInterval: (query) => {
      const accounts = query.state.data?.accounts ?? [];
      return accounts.some((a) => a.status === 'syncing') ? 5_000 : false;
    },
  });

  const vendorLinksQuery = useQuery({
    queryKey: ADS_VENDOR_LINKS_QUERY_KEY,
    queryFn: async () => {
      const res = await connectionsApi.listAdsVendorLinks();
      if (!res.success) throw new Error('Failed to load retail account links');
      return res.data;
    },
    enabled: Boolean(user?.id) && tab === 'ads' && !isDemo,
    staleTime: 30_000,
  });

  const vendorLinksByProfile = useMemo(() => {
    const map = new Map<string, AdsVendorLinkProfile>();
    for (const profile of vendorLinksQuery.data?.profiles ?? []) {
      map.set(profile.profileId, profile);
    }
    return map;
  }, [vendorLinksQuery.data]);

  const replaceVendorLinksMutation = useMutation({
    mutationFn: async ({
      profileId,
      credentialIds,
    }: {
      profileId: string;
      credentialIds: string[];
    }) => {
      const res = await connectionsApi.replaceAdsVendorLinks(profileId, credentialIds);
      if (!res.success) throw new Error('Failed to save retail account mapping');
      return res.data;
    },
    onSuccess: async () => {
      showToast('Retail account mapping saved', 'success');
      await queryClient.invalidateQueries({ queryKey: ADS_VENDOR_LINKS_QUERY_KEY });
    },
    onError: (err) => {
      showToast((err as Error).message || 'Could not save retail account mapping', 'error');
    },
  });

  useEffect(() => {
    const nextTab = parseTab(searchParams.get('tab'));
    setTab(nextTab);
  }, [searchParams]);

  const setTabAndUrl = (next: ConnectionsTab) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    const amazon = searchParams.get('amazon');
    if (!amazon) return;
    setTabAndUrl('ads');
    if (amazon === 'connected') {
      showToast('Amazon Ads connected — choose which accounts to set up', 'success');
      void refetch();
    } else if (amazon === 'error') {
      showToast(searchParams.get('message') || 'Amazon authorization failed', 'error');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('amazon');
    next.delete('message');
    next.delete('credential_id');
    next.set('tab', 'ads');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot OAuth callback handling
  }, [searchParams]);

  useEffect(() => {
    const flipkart = searchParams.get('flipkart');
    if (!flipkart) return;
    if (flipkart === 'connected') {
      showToast('Flipkart seller connected', 'success');
      void queryClient.invalidateQueries({ queryKey: FLIPKART_OAUTH_CONNECTIONS_QUERY_KEY });
    } else if (flipkart === 'error') {
      showToast(searchParams.get('message') || 'Flipkart authorization failed', 'error');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('flipkart');
    next.delete('message');
    next.delete('credential_id');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, showToast, queryClient]);

  useEffect(() => {
    const flipkartAds = searchParams.get('flipkart_ads');
    if (!flipkartAds) return;
    if (flipkartAds === 'connected') {
      showToast(flipkartAdsConnectMessage(searchParams.get('reused') === '1'), 'success');
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_OAUTH_CONNECTIONS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY });
    } else if (flipkartAds === 'error') {
      showToast(searchParams.get('message') || 'Flipkart Ads authorization failed', 'error');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('flipkart_ads');
    next.delete('message');
    next.delete('credential_id');
    next.delete('reused');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, showToast, queryClient]);

  useEffect(() => {
    const spapi = searchParams.get('spapi');
    if (!spapi) return;
    const typeParam = searchParams.get('type');
    const nextTab: ConnectionsTab =
      typeParam === 'vendor' || searchParams.get('tab') === 'vendor' ? 'vendor' : 'seller';
    const credentialId = searchParams.get('credential_id');
    if (credentialId) setPreferredSpCredentialId(credentialId);
    setTab(nextTab);
    if (spapi === 'connected') {
      const created = searchParams.get('created') !== '0';
      const vendorToast = created
        ? 'Vendor Central connected — enable it on this tab to start the data backfill'
        : 'Already connected — updated existing Vendor account';
      const sellerToast = created
        ? 'Seller Central connected — accounts and ASINs will appear shortly'
        : 'Already connected — updated existing Seller account';
      showToast(nextTab === 'vendor' ? vendorToast : sellerToast, 'success');
      void queryClient.invalidateQueries({ queryKey: SP_API_OAUTH_CONNECTIONS_QUERY_KEY });
    } else if (spapi === 'error') {
      showToast(searchParams.get('message') || 'Seller/Vendor authorization failed', 'error');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('spapi');
    next.delete('message');
    next.delete('credential_id');
    next.delete('type');
    next.delete('created');
    next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot OAuth callback handling
  }, [searchParams]);

  const amazonAccounts = useMemo(() => {
    const group = data?.marketplaces?.find((m) => m.marketplace === 'amazon_ads');
    return group?.accounts ?? data?.accounts ?? [];
  }, [data]);

  const selectableIds = useMemo(
    () =>
      amazonAccounts
        .filter((a) => a.status !== 'ready' && a.status !== 'syncing' && !a.alreadySynced)
        .map((a) => a.externalAccountId),
    [amazonAccounts]
  );

  const accountGroups = useMemo(
    () =>
      groupByAdvertisingRegion(amazonAccounts, (account) =>
        amazonAccountRegion({
          countryCode: account.countryCode,
          marketplaceId: account.marketplaceId ?? undefined,
        })
      ),
    [amazonAccounts]
  );

  const setupMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await connectionsApi.setupAccounts({
        marketplace: 'amazon_ads',
        externalAccountIds: ids,
      });
      if (!res.success) throw new Error('Failed to start setup');
      return res.data;
    },
    onSuccess: async (result) => {
      const started = result.results.filter((r) => r.started).length;
      const ready = result.results.filter((r) => r.alreadyReady).length;
      if (started > 0) {
        showToast(`Setup started for ${started} account${started === 1 ? '' : 's'}`, 'success');
      }
      if (ready > 0 && started === 0) {
        showToast(
          `${ready} account${ready === 1 ? '' : 's'} already synced and enabled`,
          'success'
        );
      }
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['manager-accounts'] });
    },
    onError: (err) => {
      showToast((err as Error).message || 'Setup failed', 'error');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({
      account,
      isEnabled,
    }: {
      account: ConnectionAccount;
      isEnabled: boolean;
    }) => {
      const res = await connectionsApi.setEnabled(
        account.marketplace,
        account.externalAccountId,
        isEnabled
      );
      if (!res.success) throw new Error('Failed to update account');
      return res.data;
    },
    onSuccess: async (_data, vars) => {
      showToast(
        vars.isEnabled
          ? 'Account enabled on Advertising accounts'
          : 'Account hidden from Advertising accounts',
        'success'
      );
      await queryClient.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['manager-accounts'] });
      await queryClient.invalidateQueries({ queryKey: ['advertising-profiles'] });
    },
    onError: (err) => {
      showToast((err as Error).message || 'Could not update account', 'error');
    },
  });

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllNeedingSetup = () => {
    setSelected(new Set(selectableIds));
  };

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <header className="border-b border-border/80 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6">
          <ProductBrand variant="light" showTagline={false} />
          <nav className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2" aria-label="Account">
            <Link
              to="/accounts"
              className="text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              Advertising accounts
            </Link>
            <Link
              to="/brands"
              className="text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              Brands
            </Link>
            <Link
              to={ANALYSIS_ROUTE}
              className="text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              Analysis
            </Link>
            {showAdmin && (
              <Link
                to="/admin/users"
                className="text-sm font-medium text-foreground-muted hover:text-foreground"
              >
                Admin
              </Link>
            )}
            {isSuperAdmin ? (
              <Link
                to="/admin/settings"
                className="text-sm font-medium text-foreground-muted hover:text-foreground"
              >
                SP-API ops
              </Link>
            ) : null}
            <Link
              to="/dashboard"
              className="text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              Dashboard
            </Link>
            <SignOutButton />
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-brand-ink sm:text-4xl">
          Connections
        </h1>
        <p className="mt-2 text-sm text-foreground-muted sm:text-[15px]">
          Connect your advertising, seller and vendor accounts to start analysing your data.
        </p>
        <div
          role="tablist"
          aria-label="Connection type"
          className="mt-6 flex gap-1 rounded-xl border border-border/80 bg-white p-1.5 shadow-sm"
        >
          {TABS.map((item) => {
            const selectedTab = tab === item.id;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selectedTab}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-3 text-sm font-medium transition-colors',
                  selectedTab
                    ? 'bg-brand text-white shadow-sm'
                    : 'text-foreground-muted hover:bg-[#f7f8fa] hover:text-foreground'
                )}
                onClick={() => setTabAndUrl(item.id)}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {item.label}
              </button>
            );
          })}
        </div>

        {!isDemo && tab === 'ads' ? (
          <div className="mt-5">
            <AmazonOauthBanner />
          </div>
        ) : null}

        {tab === 'ads' ? (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Spinner className="w-10 h-10" />
              </div>
            ) : error ? (
              <div className="max-w-lg mx-auto bg-surface rounded-xl border border-error/20 p-6 text-center">
                <AlertCircle className="w-10 h-10 text-error mx-auto mb-3" />
                <p className="text-foreground font-medium mb-2">
                  {isAmazonReauthRequiredError(error)
                    ? 'Amazon reconnection required'
                    : 'Could not load connections'}
                </p>
                <p className="text-sm text-foreground-muted mb-4">
                  {isAmazonReauthRequiredError(error)
                    ? 'Your Amazon Ads refresh token is invalid or expired. Connect Amazon again to restore access.'
                    : (error as Error).message}
                </p>
                {isAmazonReauthRequiredError(error) && !isDemo ? (
                  <ConnectAmazonButton />
                ) : (
                  <Button type="button" onClick={() => refetch()}>
                    Retry
                  </Button>
                )}
              </div>
            ) : (
              <section
                className="overflow-hidden rounded-2xl border border-[#eadfce]/80 bg-white shadow-sm"
                aria-labelledby="amazon-ads-heading"
              >
                <div className="flex flex-wrap items-center justify-between gap-4 bg-[#fbf8f3] px-4 py-4 sm:px-6 sm:py-5">
                  <div className="flex min-w-0 items-start gap-3">
                    <img
                      src="/amazon.svg"
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-xl border border-black/5 bg-white p-1.5 shadow-sm"
                    />
                    <div className="min-w-0">
                      <h2 id="amazon-ads-heading" className="text-base font-semibold text-brand-ink">
                        Amazon Ads
                      </h2>
                      <p className="mt-0.5 max-w-md text-sm leading-snug text-foreground-muted">
                        Connect your Amazon Advertising accounts to analyse performance and automate
                        optimization.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    {!isDemo ? (
                      <ConnectAmazonButton
                        showConnectButton={false}
                        regionOptions={CONNECTIONS_AMAZON_REGION_OPTIONS}
                        regionSelection={amazonRegion}
                        onRegionSelectionChange={setAmazonRegion}
                      />
                    ) : null}
                    {!isDemo ? (
                      <ConnectAmazonButton
                        showRegion={false}
                        realm={amazonRealm}
                        className={connectButtonClass}
                      />
                    ) : null}
                  </div>
                </div>

                <div className="bg-white px-4 pb-4 pt-1 sm:px-5 sm:pb-5">
                  {amazonAccounts.length === 0 ? (
                    <div className="flex min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-[#d5dbe3] bg-white px-6 py-10 text-center">
                      <ConnectionEmptyMark src="/amazon.svg" />
                      <p className="mt-4 text-sm font-semibold text-brand-ink">
                        {isDemo
                          ? 'No demo accounts are available.'
                          : 'No Amazon accounts connected yet.'}
                      </p>
                      {!isDemo ? (
                        <p className="mt-1 max-w-md text-sm text-foreground-muted">
                          Connect your Amazon Advertising account to get started with insights and
                          automation.
                        </p>
                      ) : null}
                      {!isDemo ? (
                        <div className="mt-4">
                          <ConnectAmazonButton
                            showRegion={false}
                            realm={amazonRealm}
                            className={connectButtonClass}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {accountGroups.map((group, groupIndex) => (
                        <section key={group.id}>
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <CountryHeading code={group.id} label={group.label} />
                            {groupIndex === 0 ? (
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {selectableIds.length > 0 && (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={selectAllNeedingSetup}
                                  >
                                    Select needing setup
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={selected.size === 0 || setupMutation.isPending}
                                  onClick={() => setupMutation.mutate([...selected])}
                                >
                                  {setupMutation.isPending ? (
                                    <>
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden />
                                      Starting…
                                    </>
                                  ) : (
                                    `Start setup${selected.size ? ` (${selected.size})` : ''}`
                                  )}
                                </Button>
                              </div>
                            ) : null}
                          </div>
                    <ul className="space-y-2">
                      {group.items.map((account) => {
                        const needsSetup =
                          account.status !== 'ready' &&
                          account.status !== 'syncing' &&
                          !account.alreadySynced;
                        const canToggle = account.status === 'ready' || account.alreadySynced;
                        const label =
                          account.accountName?.trim() ||
                          account.externalAccountId ||
                          'Untitled account';
                        return (
                          <ConnectionAccountCard
                            key={account.externalAccountId}
                            title={label}
                            leading={
                              needsSetup ? (
                                <input
                                  type="checkbox"
                                  className="mt-1"
                                  checked={selected.has(account.externalAccountId)}
                                  onChange={() => toggleSelect(account.externalAccountId)}
                                  aria-label={`Select ${label}`}
                                />
                              ) : undefined
                            }
                            actions={
                              <>
                                {!isDemo ? (
                                  <AdsVendorLinkPicker
                                    profileId={account.profileId}
                                    adsMarketplaceId={account.marketplaceId}
                                    linkedVendors={
                                      vendorLinksByProfile.get(account.profileId)?.vendors ?? []
                                    }
                                    availableVendors={
                                      vendorLinksQuery.data?.availableVendors ?? []
                                    }
                                    saving={
                                      replaceVendorLinksMutation.isPending &&
                                      replaceVendorLinksMutation.variables?.profileId ===
                                        account.profileId
                                    }
                                    onSave={(profileId, credentialIds) =>
                                      replaceVendorLinksMutation.mutate({
                                        profileId,
                                        credentialIds,
                                      })
                                    }
                                  />
                                ) : null}
                                {account.status === 'failed' && (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    disabled={setupMutation.isPending}
                                    onClick={() =>
                                      setupMutation.mutate([account.externalAccountId])
                                    }
                                  >
                                    Retry setup
                                  </Button>
                                )}
                                {canToggle && (
                                  <label className="inline-flex items-center gap-2 text-sm text-foreground-muted cursor-pointer">
                                    <span>Enabled</span>
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={account.isEnabled}
                                      disabled={toggleMutation.isPending}
                                      onClick={() =>
                                        toggleMutation.mutate({
                                          account,
                                          isEnabled: !account.isEnabled,
                                        })
                                      }
                                      className={cn(
                                        'relative h-6 w-11 rounded-full transition-colors',
                                        account.isEnabled ? 'bg-brand' : 'bg-border'
                                      )}
                                    >
                                      <span
                                        className={cn(
                                          'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-surface shadow transition-transform',
                                          account.isEnabled && 'translate-x-5'
                                        )}
                                      />
                                    </button>
                                  </label>
                                )}
                              </>
                            }
                          >
                            <p className="mt-0.5 truncate text-xs text-foreground-subtle">
                              {[account.accountType, account.marketplaceId, account.profileId]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                            {account.managerAccountName ? (
                              <p className="mt-0.5 truncate text-xs text-foreground-muted">
                                Manager: {account.managerAccountName}
                              </p>
                            ) : null}
                            <p className={cn('mt-1 text-xs', statusClass(account))}>
                              {statusLabel(account)}
                              {account.lastError ? ` — ${account.lastError}` : ''}
                            </p>
                          </ConnectionAccountCard>
                        );
                      })}
                    </ul>
                        </section>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {!isDemo ? (
              <div className="mt-5">
                <FlipkartAdsConnectionsPanel />
              </div>
            ) : null}
          </>
        ) : null}

        {tab === 'seller' ? (
          <div className="mt-6 space-y-5">
            {!isDemo ? (
              <SpVerticalConnectionsPanel
                type="seller"
                preferredCredentialId={preferredSpCredentialId}
              />
            ) : null}
            <section aria-labelledby="flipkart-connections-heading">
              <h2 id="flipkart-connections-heading" className="sr-only">
                Flipkart seller connections
              </h2>
              <FlipkartConnectionsPanel />
            </section>
          </div>
        ) : null}

        {tab === 'vendor' && !isDemo ? (
          <div className="mt-6">
            <SpVerticalConnectionsPanel
              type="vendor"
              preferredCredentialId={preferredSpCredentialId}
            />
          </div>
        ) : null}

        {(tab === 'seller' || tab === 'vendor') && isDemo ? (
          <div className="mt-6 rounded-2xl border border-border bg-white p-8 text-center text-foreground-muted shadow-sm">
            Seller and Vendor Central connections are not available on demo accounts.
          </div>
        ) : null}
      </main>
    </div>
  );
}
